import { useEffect, useRef } from "react";
import { notification } from "antd";
import { InstagramFilled, FacebookFilled } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import callAPI from "../fetch/main";

// Global live-chat listener.
//
// Mounted once at the app root so it runs on EVERY page (not just Live Chat).
// At launch it discovers the account/page ids (from the conversation lists,
// IG + FB) and opens one persistent Cloudflare WebSocket per id. When an
// incoming message is pushed, it shows a toast anywhere in the app and plays a
// short tone. The Live Chat page still opens its own sockets for the in-thread
// rendering; this one is purely for app-wide notification.
//
// No-op when logged out (no token). Reconnects with backoff per socket.

const WS_BASE = "wss://idm-live-chat-websocket.sanjayhotaction.workers.dev/ws";

export default function GlobalLiveChat() {
  const navigate = useNavigate();
  const socketsRef = useRef({});   // id -> WebSocket
  const timersRef = useRef({});    // id -> reconnect timer
  const retriesRef = useRef({});   // id -> attempt count
  const recentRef = useRef(new Set()); // seen message ids (dedupe toasts)
  const fbIdsRef = useRef(new Set());  // ids that belong to the Facebook page

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) return; // not logged in — nothing to do

    let cancelled = false;

    // Which account ids are the Facebook page (so a message received on that
    // socket is Facebook, everything else is Instagram). Seeded from userData.
    try {
      const ud = JSON.parse(localStorage.getItem("userData") || "{}");
      const fp = ud?.fb_default_page || {};
      if (fp?.id) fbIdsRef.current.add(String(fp.id));
    } catch (_) { /* ignore */ }

    // --- notification helpers ---
    const playTone = () => {
      try {
        const AudioCtx = window.AudioContext || window.webkitAudioContext;
        if (!AudioCtx) return;
        const ctx = new AudioCtx();
        const o = ctx.createOscillator();
        const g = ctx.createGain();
        o.connect(g); g.connect(ctx.destination);
        o.frequency.value = 880;
        g.gain.setValueAtTime(0.06, ctx.currentTime);
        g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.25);
        o.start(); o.stop(ctx.currentTime + 0.25);
      } catch (_) { /* autoplay blocked — ignore */ }
    };

    const notify = (msg, receivedOnId) => {
      // Only toast for genuine inbound customer messages.
      if (msg?.type !== "incoming_message" && msg?.type !== "incoming" && msg?.type !== "chat") return;
      if (msg?.from_automation === true || msg?.from_echo === true) return;

      const sender = msg.sender_id || msg.from || msg?.sender?.id || "";
      const text =
        msg.message?.text ||
        (typeof msg.message === "string" ? msg.message : null) ||
        msg.text || msg.content || "New message";

      // Real sender name (resolved by the backend), with safe fallbacks.
      const name =
        msg.sender_name ||
        msg.message?.sender_name ||
        msg.message?.from?.username ||
        msg.sender_username ||
        msg.username ||
        (sender ? `User ${String(sender).slice(-4)}` : "New contact");

      // Platform / app logo. Decide in order of reliability:
      //   1. The socket it arrived on is the known Facebook page id -> Facebook.
      //   2. The backend explicitly tagged the platform -> trust it.
      //   3. Otherwise assume Instagram.
      const backendPlatform = (msg.platform || msg.message?.platform || "").toLowerCase();
      let isFacebook;
      if (receivedOnId && fbIdsRef.current.has(String(receivedOnId))) {
        isFacebook = true;
      } else if (backendPlatform === "facebook") {
        isFacebook = true;
      } else if (backendPlatform === "instagram") {
        isFacebook = false;
      } else {
        isFacebook = false; // default to Instagram
      }
      const platformIcon = isFacebook
        ? <FacebookFilled style={{ color: "#1877F2", fontSize: 22 }} />
        : <InstagramFilled style={{ color: "#E1306C", fontSize: 22 }} />;
      const platformLabel = isFacebook ? "Facebook" : "Instagram";

      // Dedupe by message id (sockets for multiple ids may each deliver it).
      const mid = msg.message?.mid || msg.message?.id || msg.mid || msg.id || `${sender}-${text}`;
      if (recentRef.current.has(mid)) return;
      recentRef.current.add(mid);
      if (recentRef.current.size > 200) {
        recentRef.current = new Set(Array.from(recentRef.current).slice(-100));
      }

      // Toast on EVERY page, including the Live Chat / chat pages, so a new
      // message is always surfaced even while the inbox is open.
      playTone();
      const targetPlatform = isFacebook ? "facebook" : "instagram";
      const notifKey = `lc-${mid}`;
      const openChat = () => {
        // Hand the target to the Live Chat page so it auto-opens that exact
        // thread. sessionStorage survives the route change; the query string is
        // a visible backup.
        try {
          sessionStorage.setItem("openLiveChat", JSON.stringify({
            senderId: String(sender || ""),
            platform: targetPlatform,
            at: Date.now(),
          }));
        } catch (_) { /* ignore */ }
        notification.destroy(notifKey);
        navigate(`/live-chat?sender=${encodeURIComponent(String(sender || ""))}&platform=${targetPlatform}`);
      };
      notification.open({
        key: notifKey,
        message: (
          <span>
            {name}{" "}
            <span style={{ fontSize: 11, fontWeight: 400, color: "#999" }}>· {platformLabel}</span>
          </span>
        ),
        description: String(text).slice(0, 140),
        icon: platformIcon,
        placement: "topRight",
        duration: 6,
        onClick: openChat,
        style: { cursor: "pointer" },
      });
    };

    const connect = (id) => {
      if (cancelled || !id) return;
      const existing = socketsRef.current[id];
      if (existing && existing.readyState <= WebSocket.OPEN) return;

      const ws = new WebSocket(`${WS_BASE}?sessionId=${id}&username=${id}`);
      socketsRef.current[id] = ws;

      ws.onopen = () => { retriesRef.current[id] = 0; console.log(`[GlobalLiveChat] open ${id}`); };
      ws.onmessage = (e) => {
        console.log(`[GlobalLiveChat] msg on ${id}:`, e.data);
        try { notify(JSON.parse(e.data), id); }
        catch (err) { console.error("[GlobalLiveChat] notify error:", err); }
      };
      ws.onclose = () => {
        if (cancelled) return;
        const n = (retriesRef.current[id] = (retriesRef.current[id] || 0) + 1);
        const delay = Math.min(1000 * Math.pow(2, n), 30000);
        timersRef.current[id] = setTimeout(() => connect(id), delay);
      };
      ws.onerror = () => { /* onclose fires next */ };
    };

    // Discover the account/page ids from the conversation lists (IG + FB).
    // participants.data[0].id is the page/account id — the exact id the backend
    // forwards incoming messages to.
    const discoverAndConnect = () => {
      const ids = new Set();

      // Seed with the account's OWN ids from the logged-in user data, so we are
      // subscribed to both Instagram and Facebook from launch — even for an
      // account that has no conversations yet (a brand-new sender's first DM is
      // forwarded to the IG account id / FB page id regardless of history).
      try {
        const ud = JSON.parse(localStorage.getItem("userData") || "{}");
        const dp = ud?.default_page || {};
        if (dp?.id) ids.add(String(dp.id));            // IG account page id
        if (dp?.user_id) ids.add(String(dp.user_id));  // IG account (IGSID)
        const fp = ud?.fb_default_page || {};
        if (fp?.id) ids.add(String(fp.id));            // FB page id
        const cia = fp?.connected_instagram_account || {};
        if (cia?.id) ids.add(String(cia.id));          // FB-connected IG id
        // Connect to the seed ids immediately (don't wait for the lists).
        Array.from(ids).forEach(connect);
      } catch (_) { /* ignore malformed userData */ }

      // Connect each list's ids as soon as that list returns — don't gate IG on
      // the FB call (or vice versa), so a slow/empty/failed list for one
      // platform never blocks notifications for the other.
      callAPI({
        url: "ig-messages", method: "GET", loadingState: () => {},
        dataState: (d) => {
          (d?.data || []).forEach((c) => {
            const pid = c?.participants?.data?.[0]?.id;
            if (pid) { ids.add(String(pid)); connect(String(pid)); }
          });
        },
      });
      callAPI({
        url: "ig-messages?fb_page=true", method: "GET", loadingState: () => {},
        dataState: (d) => {
          (d?.data || []).forEach((c) => {
            const pid = c?.participants?.data?.[0]?.id;
            if (pid) {
              ids.add(String(pid));
              fbIdsRef.current.add(String(pid)); // this id is the Facebook page
              connect(String(pid));
            }
          });
        },
      });
    };

    discoverAndConnect();

    return () => {
      cancelled = true;
      Object.values(timersRef.current).forEach(clearTimeout);
      Object.values(socketsRef.current).forEach((ws) => { try { ws.close(); } catch (_) {} });
      socketsRef.current = {};
      timersRef.current = {};
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return null; // headless
}
